import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-transparent border-b border-sage/30 focus:border-sage-deep py-3 text-ink placeholder:text-charcoal/50 outline-none transition-colors duration-300 text-base";
const label = "block text-[11px] tracking-[0.2em] uppercase font-semibold text-sage-deep mb-1";

export default function IntakeForm({ initialSituation = "" }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", situation: initialSituation, preferred_contact: "", preferred_time: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim() && !form.phone.trim()) {
      setError("Please share an email or phone number so we can reach you — either one is enough.");
      return;
    }
    setStatus("sending");
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim() !== ""));
      await axios.post(`${API}/intake`, payload);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong on our end. Please try again, or simply call or text us instead.");
    }
  };

  return (
    <div data-testid="intake-form-wrapper">
      <AnimatePresence mode="wait">
        {status === "sent" ? (
          <motion.div
            key="sent"
            data-testid="intake-success-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-center py-16"
          >
            <ShieldCheck size={40} strokeWidth={1.25} className="mx-auto text-sage-deep mb-6" />
            <h3 className="font-serif text-3xl sm:text-4xl text-night">Thank you for trusting us.</h3>
            <p className="mt-4 text-charcoal leading-relaxed max-w-md mx-auto">
              Your note arrived privately. A member of our Central Florida team will reach out the
              way you asked — usually within one business day. No pressure, ever.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5 }}
            noValidate
          >
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
              <div>
                <label htmlFor="intake-name" className={label}>Name <span className="normal-case font-normal text-charcoal/60">(first name is fine)</span></label>
                <input id="intake-name" data-testid="intake-name-input" type="text" value={form.name} onChange={set("name")} placeholder="Jane" className={field} autoComplete="name" />
              </div>
              <div>
                <label htmlFor="intake-email" className={label}>Email</label>
                <input id="intake-email" data-testid="intake-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={field} autoComplete="email" />
              </div>
              <div>
                <label htmlFor="intake-phone" className={label}>Phone</label>
                <input id="intake-phone" data-testid="intake-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(407) 555-0000" className={field} autoComplete="tel" />
              </div>
              <div>
                <label htmlFor="intake-situation" className={label}>What's happening with the property?</label>
                <select id="intake-situation" data-testid="intake-situation-select" value={form.situation} onChange={set("situation")} className={`${field} ${form.situation ? "" : "text-charcoal/50"}`}>
                  <option value="">Choose, if you'd like</option>
                  <option>Short sale</option>
                  <option>Foreclosure concerns</option>
                  <option>Inherited or vacant property</option>
                  <option>Liens or judgments</option>
                  <option>Title issues</option>
                  <option>Something else / not sure</option>
                  <option>Prefer not to say</option>
                </select>
              </div>
              <div>
                <label htmlFor="intake-contact" className={label}>How should we reach you?</label>
                <select id="intake-contact" data-testid="intake-contact-method-select" value={form.preferred_contact} onChange={set("preferred_contact")} className={`${field} ${form.preferred_contact ? "" : "text-charcoal/50"}`}>
                  <option value="">Choose, if you'd like</option>
                  <option>A phone call</option>
                  <option>A text message</option>
                  <option>Email</option>
                  <option>Any of these</option>
                </select>
              </div>
              <div>
                <label htmlFor="intake-time" className={label}>Best time for a confidential consultation</label>
                <input id="intake-time" data-testid="intake-time-input" type="text" value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g., Weekday mornings" className={field} />
              </div>
            </div>
            <div className="mt-8">
              <label htmlFor="intake-message" className={label}>Anything you'd like us to know <span className="normal-case font-normal text-charcoal/60">(optional)</span></label>
              <textarea id="intake-message" data-testid="intake-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Share only what feels comfortable." className={`${field} resize-none`} />
            </div>

            {error && (
              <p data-testid="intake-error-message" className="mt-6 text-sm text-[#9C4A3C]">{error}</p>
            )}

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <button
                type="submit"
                data-testid="intake-submit-button"
                disabled={status === "sending"}
                className="rounded-full bg-night text-ivory px-10 py-4 text-sm tracking-wide hover:bg-sage-deep hover:scale-[1.02] transition-all duration-300 disabled:opacity-60 disabled:hover:scale-100 flex items-center gap-3"
              >
                {status === "sending" && <Loader2 size={16} className="animate-spin" />}
                {status === "sending" ? "Sending privately…" : "Send privately"}
              </button>
              <p className="text-xs text-charcoal/70 leading-relaxed max-w-xs">
                Your information goes only to our local team. It is never sold, shared, or used to pressure you.
              </p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
